import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { ReactElement, useCallback } from 'react';

import { useGetAttendanceTimesheet } from '@/modules/workforce/workersTimesheet/apiHooks';
import { VerifyWorkerImage } from '@/modules/workforce/workersTimesheet/components/Modals/VerifyWorkerImage';
import { timesheetTableColumns } from '@/modules/workforce/workersTimesheet/components/TimesheetTable/logic';
import { IWorkersTimesheet } from '@/modules/workforce/workersTimesheet/types';
import { Loading, Table } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import { EmptyCard } from '@/shared/components/molecules';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import CloudFence from '~/images/shared/CloudFence.svg';

const PER_PAGE = 10;

interface ITimesheetTableProps {
  date: string;
}

export default function TimesheetTable({
  date,
}: ITimesheetTableProps): ReactElement {
  const [store] = useStore((store) => store);
  const { t } = useTranslation('common');
  const { openModal } = useModal();
  const { locale, push } = useRouter();
  const [page, setPage] = React.useState(1);
  const onPageChange = (page: number) => {
    setPage(page);
  };
  // eslint-disable-next-line no-console

  const { data, isLoading } = useGetAttendanceTimesheet({
    pageNumber: page,
    pageSize: PER_PAGE,
    date,
    entityId: store?.asset?.id,
  });

  const onClickVerifyHandler = useCallback(
    (
      attendaceRow: IWorkersTimesheet.IAttendanceTimesheetItem,
      action: 'show' | 'review'
    ) => {
      openModal(
        <VerifyWorkerImage
          attendanceRow={attendaceRow}
          date={date}
          action={action}
        />,
        {
          title: t(
            action === 'review' ? 'verify-identity' : 'captured-face-ID'
          ),
        }
      );
    },
    [date, openModal, t]
  );

  if (isLoading) {
    return (
      <div className='mt-100  '>
        <Loading />
      </div>
    );
  }
  // if no data found
  if (data?.list?.length === 0) {
    return (
      <EmptyCard
        vector={<CloudFence />}
        variant='icon'
        fullSVG
        classeName='mt-52'
        message={t('no-workers-found')}
      />
    );
  }

  // else if there are suppliers

  const handleWorkerClick = (url: string) => {
    if (store?.isDemoFarm) {
      openModal(<DemoCTA />);
    } else {
      push(url);
    }
  };

  return (
    <div className='mt-11'>
      <Table<IWorkersTimesheet.IAttendanceTimesheetItem>
        stickyFirstCloumn
        tableWrapperClassName='max-h-640'
        // rowContainerClassNameFu={(row) =>
        //   row.attendance.isFaceVerified === 0 ||
        //   row.dismissal.isFaceVerified === 0
        //     ? 'bg-error-100'
        //     : ''
        // }
        headCellClassName='ltr:text-left rtl:text-right'
        columns={timesheetTableColumns(
          t,
          handleWorkerClick,
          onClickVerifyHandler,
          locale
        )}
        data={data?.list || []}
        pagination={true}
        selection={false}
        currentPage={page}
        perPage={PER_PAGE}
        totalCount={data?.count || 0}
        onPageChange={onPageChange}
      />
    </div>
  );
}
